import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { type IntegrationsSettings } from "@/lib/onlineBooking";
import { Button } from "@/components/ui/Button";
import { FormHint } from "@/pages/stammdaten/schnittstellen/FormHint";

interface TenantSettings {
  integrations: IntegrationsSettings;
}

interface ChannelApiIntegrationPanelProps {
  canWrite: boolean;
}

export function ChannelApiIntegrationPanel({ canWrite }: ChannelApiIntegrationPanelProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [integrationApiKeys, setIntegrationApiKeys] = useState<IntegrationsSettings["api_keys"]>([]);
  const [newApiKeyName, setNewApiKeyName] = useState("");
  const [createdApiKey, setCreatedApiKey] = useState<string | null>(null);
  const [apiKeyCreating, setApiKeyCreating] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .get<TenantSettings>("/api/v1/settings")
      .then((settings) => {
        setIntegrationApiKeys(settings.integrations?.api_keys ?? []);
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function onCreateApiKey(e: React.FormEvent) {
    e.preventDefault();
    if (!canWrite || !newApiKeyName.trim()) return;
    setApiKeyCreating(true);
    setError(null);
    setCreatedApiKey(null);
    try {
      const res = await api.post<{ api_key: string }>(
        "/api/v1/settings/integrations/api-keys",
        { name: newApiKeyName.trim() },
      );
      setCreatedApiKey(res.api_key);
      setNewApiKeyName("");
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "API-Key konnte nicht erzeugt werden.");
    } finally {
      setApiKeyCreating(false);
    }
  }

  async function onDeleteApiKey(keyId: string) {
    if (!canWrite) return;
    setError(null);
    try {
      await api.del(`/api/v1/settings/integrations/api-keys/${keyId}`);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Löschen fehlgeschlagen.");
    }
  }

  if (loading) {
    return <p className="text-sm text-label-secondary">Laden…</p>;
  }

  return (
    <div className="space-y-5">
      <FormHint>
        Partner senden Buchungen an{" "}
        <code className="text-xs">POST /api/v1/integrations/bookings</code> mit Header{" "}
        <code className="text-xs">X-Api-Key</code>. Der vollständige Key wird nur einmal bei der
        Erstellung angezeigt – sicher aufbewahren.
      </FormHint>

      <ul className="space-y-2">
        {integrationApiKeys.length === 0 ? (
          <li className="rounded-xl border border-dashed border-separator px-4 py-6 text-center text-sm text-label-secondary">
            Noch keine API-Keys angelegt.
          </li>
        ) : (
          integrationApiKeys.map((k) => (
            <li
              key={k.id}
              className="flex flex-col gap-2 rounded-xl border border-separator bg-fill-quaternary px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
            >
              <span className="text-sm">
                <span className="font-medium text-label">{k.name}</span>{" "}
                <span className="text-label-tertiary">({k.key_prefix})</span>
              </span>
              {canWrite ? (
                <Button type="button" variant="secondary" size="sm" onClick={() => void onDeleteApiKey(k.id)}>
                  Entfernen
                </Button>
              ) : null}
            </li>
          ))
        )}
      </ul>

      {createdApiKey ? (
        <div className="rounded-xl border border-status-orange/40 bg-status-orange/10 px-4 py-3">
          <p className="text-sm font-medium text-label">Neuer API-Key (nur jetzt sichtbar)</p>
          <code className="mt-2 block break-all font-mono text-xs text-label-secondary">
            {createdApiKey}
          </code>
        </div>
      ) : null}

      {canWrite ? (
        <form
          onSubmit={(e) => void onCreateApiKey(e)}
          className="flex flex-col gap-3 border-t border-separator pt-4 sm:flex-row sm:items-end"
        >
          <label className="flex flex-1 flex-col gap-1.5 text-sm">
            <span className="font-medium text-label">Bezeichnung</span>
            <input
              className="eh-input"
              value={newApiKeyName}
              onChange={(e) => setNewApiKeyName(e.target.value)}
              placeholder="z. B. camping.info"
            />
          </label>
          <Button
            type="submit"
            variant="secondary"
            loading={apiKeyCreating}
            disabled={!newApiKeyName.trim()}
          >
            API-Key erzeugen
          </Button>
        </form>
      ) : null}

      {error ? <p className="text-sm text-status-red">{error}</p> : null}
    </div>
  );
}
